import type { CalendarItemDto } from './schemas-planning';

/** Słupek w wierszu dni: wpis całodniowy przez kolejne dni albo pojedynczy kafelek (wpis z godzinami). */
export interface CalendarBar {
  item: CalendarItemDto;
  /** kolumna startu (0 = pierwszy dzień wiersza) i liczba dni */
  col: number;
  span: number;
  lane: number;
  /** pierwszy / ostatni dzień tego wpisu (a nie ciąg z poprzedniego / w następnym wierszu) */
  startsHere: boolean;
  endsHere: boolean;
}

const keyOf = (i: CalendarItemDto) => `${i.kind}|${i.refId}|${i.title}|${i.status ?? ''}|${i.color ?? ''}`;
const shift = (d: string, n: number) => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const isWeekend = (d: string) => {
  const wd = new Date(`${d}T12:00:00Z`).getUTCDay();
  return wd === 0 || wd === 6;
};

/**
 * Wiersz kalendarza (jedna osoba, kolejne dni): wpisy całodniowe tej samej pracy / nieobecności w kolejnych dniach
 * łączą się w jeden słupek — także przez weekend bez wpisu (jak w Planowaniu). Wpisy z godzinami zostają kafelkami
 * jednego dnia. Słupki rozłożone na tory bez nakładania (dłuższe wyżej).
 * `all` — wszystkie wpisy tej osoby z pobranego zakresu (ciągłość na brzegach wiersza).
 */
export function calendarBars(days: string[], all: CalendarItemDto[]): CalendarBar[] {
  const has = new Set(all.filter((i) => !i.timeFrom).map((i) => `${keyOf(i)}@${i.day}`));
  const on = (i: CalendarItemDto, d: string) => has.has(`${keyOf(i)}@${d}`);
  /** wpis jest w sąsiednim dniu pracy (pomijając weekend bez wpisu) */
  const near = (i: CalendarItemDto, d: string, dir: 1 | -1) => {
    let x = shift(d, dir);
    for (let k = 0; k < 2 && isWeekend(x) && !on(i, x); k++) x = shift(x, dir);
    return on(i, x);
  };
  const runs: Omit<CalendarBar, 'lane'>[] = [];
  const used = new Set<string>();
  days.forEach((d, col) => {
    const today = all.filter((i) => i.day === d).sort((a, b) => (a.timeFrom ?? '').localeCompare(b.timeFrom ?? ''));
    for (const i of today) {
      if (i.timeFrom) {
        runs.push({ item: i, col, span: 1, startsHere: true, endsHere: true });
        continue;
      }
      if (used.has(`${keyOf(i)}@${d}`)) continue;
      let end = col;
      let k = col + 1;
      while (k < days.length) {
        if (on(i, days[k]!)) {
          end = k;
          k++;
        } else if (isWeekend(days[k]!)) k++;
        else break;
      }
      for (let c = col; c <= end; c++) used.add(`${keyOf(i)}@${days[c]}`);
      runs.push({ item: i, col, span: end - col + 1, startsHere: !near(i, d, -1), endsHere: !near(i, days[end]!, 1) });
    }
  });
  runs.sort((a, b) => a.col - b.col || b.span - a.span);
  const laneEnd: number[] = [];
  return runs.map((r) => {
    let lane = laneEnd.findIndex((e) => e < r.col);
    if (lane < 0) lane = laneEnd.length;
    laneEnd[lane] = r.col + r.span - 1;
    return { ...r, lane };
  });
}
